import React from 'react';
import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getBaseProduct } from '../../../helpers/getBaseProduct';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { throwError } from '../../../helpers/throwError';
import { ProductBuilderMachineEvent } from '../../../types';
import { Heading } from '../../Common/Heading';
import {
  Selector,
  SelectorItem,
  SelectorImageItem,
} from '../../Common/Selector';
import { SectionContent } from './SectionContent';
import { getProductStateConfig } from '../../../helpers/getProductStateConfig';
import { getBaseProductType } from '../../../helpers/getBaseProductType';
import { CurtainHelpModal } from '../CurtainHelpModal';
import { HelpTooltip } from '../../Icon';
import styled from 'styled-components';

const StyledHelpIcon = styled.span`
  width: 42px;
  height: 42px;
  padding-left: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

type OnClick = (
  configuration: string,
  send: (event: ProductBuilderMachineEvent) => void,
) => () => void;

const onClick: OnClick = (configuration, send) => () =>
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      configuration,
    },
  });

export const Configuration = () => {
  const [state, send] = useService(productBuilderMachine);
  const baseProduct = getBaseProduct(state.context);
  const productType = getBaseProductType(state.context);
  const configurationTypes = baseProduct
    ? baseProduct.metafields.configuration || []
    : [];
  const { configuration } = state.context.custom;
  const [isHelpModalOpen, setIsHelpModalOpen] = React.useState(false);

  // Only show help modal for curtains
  const isCurtain = state.context.products[0]?.type === 'Curtain';

  if (!Array.isArray(configurationTypes) || configurationTypes.length === 0) {
    if (isCurrentState('configuration', state)) {
      throwError(state);
    }
  }

  const openHelpModal = () => {
    if (isCurtain) setIsHelpModalOpen(true);
  };

  const closeHelpModal = () => {
    setIsHelpModalOpen(false);
  };

  return (
    <SectionContent>
      <StyledHeadingWrapper>
        <Heading>Configuration</Heading>
        {isCurtain && (
          <StyledHelpIcon onClick={openHelpModal}>
            <HelpTooltip />
          </StyledHelpIcon>
        )}
      </StyledHeadingWrapper>
      <Selector isSlider={true}>
        {configurationTypes.map(option => {
          const { showIcon, Icon, description } = getProductStateConfig(
            productType,
            'configuration',
            option,
          );
          return (
            <SelectorImageItem
              isSelected={option === configuration}
              onClick={onClick(option, send)}
              showIcon={showIcon}
              Icon={Icon}
              title={option}
              key={option}
              description={description}
            />
          );
        })}
      </Selector>
      {isCurtain && (
        <CurtainHelpModal
          modalIsOpen={isHelpModalOpen}
          closeModal={closeHelpModal}
          stepType="stack"
        />
      )}
    </SectionContent>
  );
};
